import {
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
    useEffect,
    useRef,
} from 'react';
import type { IClientSubscribeOptions, MqttClient } from 'mqtt';

import type { WinchMqttUser } from '../winchMqttApi';
import { buildGetPositionPayload, buildWinchStatusPayload, winchMqttTopics } from '../winchMqttApi';

import type { WinchMqttPublish } from './handleWinchMqttMessage';
import {
    scheduleSnapshotRetries,
    scheduleUnsubscribeWinchTopics,
    subscribeWinchTopics,
} from './winchMqttSubscriptions';

const VERTICAL_SCAN_STATUS_INTERVAL_MS = 60_000;
export const WINCH_STATUS_INTERVAL_MS = 300_000;
export const WINCH_BROKER_WAIT_MS = 10_000;

type WinchMqttSubscriptionInput = {
    winchId: number | null;
    winchExists: boolean;
    client: MqttClient | null;
    isConnected: boolean;
    subscribe: (
        topic: string,
        options?: IClientSubscribeOptions,
        onSettled?: (error?: Error | null) => void
    ) => void;
    unsubscribe: (topic: string, options?: IClientSubscribeOptions) => void;
    publish: WinchMqttPublish;
    userRef: MutableRefObject<WinchMqttUser>;
    messageHandlerRef: MutableRefObject<(topic: string, message: Uint8Array) => void>;
    heardRef: MutableRefObject<boolean>;
    stopSnapshotRetriesRef: MutableRefObject<() => void>;
    publishScanStatusRef: MutableRefObject<() => void>;
    setAwaitingLive: Dispatch<SetStateAction<boolean>>;
};

/**
 * Subscribes to the winch's topics while the broker is connected, attaches one message
 * listener per client, and asks for positions, status and scan status once subscribed,
 * then on an interval.
 */
export function useWinchMqttSubscription(input: WinchMqttSubscriptionInput): void {
    const {
        winchId,
        winchExists,
        client,
        isConnected,
        subscribe,
        unsubscribe,
        publish,
        userRef,
        messageHandlerRef,
        heardRef,
        stopSnapshotRetriesRef,
        publishScanStatusRef,
        setAwaitingLive,
    } = input;
    const messageListenerRef = useRef<((topic: string, message: Uint8Array) => void) | null>(null);
    const attachedClientRef = useRef<typeof client>(null);
    const detachListenerTimerRef = useRef<number | null>(null);
    const readyRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!winchExists || winchId == null) {
            setAwaitingLive(false);
            return;
        }
        if (!isConnected || !client) {
            setAwaitingLive(true);
            const waitForBroker = window.setTimeout(() => {
                setAwaitingLive(false);
            }, WINCH_BROKER_WAIT_MS);
            return () => {
                window.clearTimeout(waitForBroker);
            };
        }

        heardRef.current = false;
        setAwaitingLive(true);

        const topics = winchMqttTopics(winchId);
        const onMessage = (topic: string, message: Uint8Array): void => {
            messageHandlerRef.current(topic, message);
        };
        const subscribedTopics = Object.values(topics.subscribe);
        // A strict-mode remount cancels the detach so the listener that receives
        // retained online/telemetry stays attached. A replaced MQTT client must
        // get its own listener; keeping the old reference would drop every message.
        if (detachListenerTimerRef.current != null) {
            window.clearTimeout(detachListenerTimerRef.current);
            detachListenerTimerRef.current = null;
        }
        if (messageListenerRef.current != null && attachedClientRef.current !== client) {
            attachedClientRef.current?.off('message', messageListenerRef.current);
            messageListenerRef.current = null;
            attachedClientRef.current = null;
        }
        if (messageListenerRef.current == null) {
            messageListenerRef.current = onMessage;
            attachedClientRef.current = client;
            client.on('message', onMessage);
        }
        let scanStatusPoll = 0;
        let winchStatusPoll = 0;
        const requestScanStatus = (): void => {
            publishScanStatusRef.current();
        };
        const requestWinchStatus = (): void => {
            publish(topics.publish.status, buildWinchStatusPayload(userRef.current), { qos: 1 });
        };
        const requestPositions = (): void => {
            publish(topics.publish.getPosition, buildGetPositionPayload(userRef.current), {
                qos: 1,
            });
        };
        const publishSnapshots = (): void => {
            requestPositions();
            requestWinchStatus();
            requestScanStatus();
        };
        let pollsStarted = false;
        const requestSnapshots = (): void => {
            publishSnapshots();
            if (pollsStarted) {
                return;
            }
            pollsStarted = true;
            scanStatusPoll = window.setInterval(
                requestScanStatus,
                VERTICAL_SCAN_STATUS_INTERVAL_MS
            );
            winchStatusPoll = window.setInterval(() => {
                requestWinchStatus();
                if (!heardRef.current) {
                    requestPositions();
                }
            }, WINCH_STATUS_INTERVAL_MS);
            stopSnapshotRetriesRef.current = scheduleSnapshotRetries(
                publishSnapshots,
                () => heardRef.current,
                () => setAwaitingLive(false)
            );
        };
        readyRef.current = requestSnapshots;
        // Publish only after every topic is subscribed. A failed subscribe retries
        // and must not request snapshots the broker would drop. The callback goes
        // through readyRef so a SUBACK from a cancelled strict-mode subscribe still
        // reaches the generation that is mounted now. A SUBACK from a replaced client
        // is ignored: the new client's topics are not subscribed yet.
        const cancelSubscribe = subscribeWinchTopics(subscribedTopics, subscribe, () => {
            if (attachedClientRef.current === client) {
                readyRef.current?.();
            }
        });

        return () => {
            if (readyRef.current === requestSnapshots) {
                readyRef.current = null;
            }
            cancelSubscribe();
            stopSnapshotRetriesRef.current();
            stopSnapshotRetriesRef.current = () => undefined;
            window.clearInterval(scanStatusPoll);
            window.clearInterval(winchStatusPoll);
            const listener = messageListenerRef.current;
            detachListenerTimerRef.current = window.setTimeout(() => {
                if (listener) {
                    client.off('message', listener);
                }
                if (messageListenerRef.current === listener) {
                    messageListenerRef.current = null;
                    attachedClientRef.current = null;
                }
                detachListenerTimerRef.current = null;
            }, 0);
            scheduleUnsubscribeWinchTopics(subscribedTopics, unsubscribe);
        };
    }, [
        isConnected,
        subscribe,
        unsubscribe,
        publish,
        client,
        winchExists,
        winchId,
        // Refs and a state setter: stable, listed so the rule can see them.
        heardRef,
        messageHandlerRef,
        publishScanStatusRef,
        setAwaitingLive,
        stopSnapshotRetriesRef,
        userRef,
    ]);
}
